// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import { Pressable, Text, View, type TextStyle, type ViewStyle } from 'react-native';
import { tokens } from '../design-system/tokens';
import { useColorScheme } from '../foundations/colorScheme';
import { shareLocationStrings } from './strings';

export interface ShareLocationCardProps {
  /**
   * Whether the shared location is a live share. Drives the label
   * shown on the card. The card NEVER renders raw lat/lng.
   */
  readonly isLive?: boolean;
  /**
   * Optional human-readable address line (e.g. "Friedrichstr. 1,
   * Berlin"). The host is responsible for omitting precise / sensitive
   * substrings.
   */
  readonly address?: string;
  /** Optional ISO-8601 timestamp for live shares — when the share ends. */
  readonly liveExpiresAt?: string;
  readonly onOpen?: () => void;
}

/**
 * In-chat bubble for a sent / received location. By contract this
 * component never displays coordinates — it shows the privacy-
 * preserving "Shared a location" label and an optional human-readable
 * address line. The map preview / open-in-map action is host-provided
 * via `onOpen`.
 */
export const ShareLocationCard: React.FC<ShareLocationCardProps> = ({
  isLive = false,
  address,
  liveExpiresAt,
  onOpen,
}) => {
  const scheme = useColorScheme();
  const colors = tokens.color.semantic[scheme];

  const handlePress = useCallback(() => {
    onOpen?.();
  }, [onOpen]);

  const title = isLive
    ? shareLocationStrings.card.obfuscatedLive
    : shareLocationStrings.card.obfuscatedPoint;

  const expiry = isLive && liveExpiresAt ? formatExpiry(liveExpiresAt) : null;

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={onOpen ? shareLocationStrings.card.openLabel : title}
      style={[
        styles.card,
        {
          borderColor: colors.border.secondary,
          backgroundColor: colors.surface.secondary,
        },
      ]}
    >
      <View
        style={[styles.preview, { backgroundColor: colors.surface.tertiary }]}
        accessibilityElementsHidden
        importantForAccessibility="no"
      />
      <View style={styles.copy}>
        <Text style={[styles.title, { color: colors.text.primary }]}>{title}</Text>
        {address ? (
          <Text
            style={[styles.address, { color: colors.text.secondary }]}
            numberOfLines={2}
          >
            {address}
          </Text>
        ) : null}
        {expiry ? (
          <Text style={[styles.expiry, { color: colors.accent.indigo }]}>
            {`${shareLocationStrings.card.liveActive} ${expiry}`}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
};

function formatExpiry(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const styles = {
  card: {
    flexDirection: 'row',
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    overflow: 'hidden',
    minHeight: 92,
    gap: tokens.spacing.md,
  } satisfies ViewStyle,
  preview: {
    width: 92,
    minHeight: 92,
  } satisfies ViewStyle,
  copy: {
    flex: 1,
    padding: tokens.spacing.md,
    gap: tokens.spacing.xxs,
  } satisfies ViewStyle,
  title: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.body,
    fontWeight: '600' as TextStyle['fontWeight'],
  } satisfies TextStyle,
  address: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
  } satisfies TextStyle,
  expiry: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    fontWeight: '600' as TextStyle['fontWeight'],
  } satisfies TextStyle,
};
